import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import OnboardingNewsletterModal, {
  OnboardingSubscribeResult,
} from '@/components/account/onboarding-newsletter-modal';
import ProfileSaveSuccessModal from '@/components/account/profile/save-success-modal';
import useVariables from '@/lib/hooks/use-variables';
import {
  getOnboardingNewsletterImage,
  getOnboardingNewsletterTexts,
} from '@/lib/onboarding/onboarding-newsletter-texts';
import {
  selectIsUserDataInitialized,
  selectShouldShowOnboardingNewsletter,
  selectUserProfile,
  setShouldShowOnboardingNewsletter,
} from '@/store/slices/user';
import { selectVariables } from '@/store/slices/variables';

type OnboardingStatusResponse = {
  shouldShow: boolean;
};

const parseStatus = (body: unknown): OnboardingStatusResponse | null => {
  if (!body || typeof body !== 'object') {
    return null;
  }

  return {
    shouldShow: (body as { shouldShow?: unknown }).shouldShow === true,
  };
};

const isEligibleProfile = ({
  email,
  emailVerified,
  firstName,
  lastName,
}: {
  email?: string;
  emailVerified?: boolean;
  firstName?: string;
  lastName?: string;
}): boolean =>
  Boolean(email && emailVerified && firstName?.trim() && lastName?.trim());

const OnboardingNewsletterGate: FC = () => {
  const dispatch = useDispatch();
  const profile = useSelector(selectUserProfile);
  const isDataInitialized = useSelector(selectIsUserDataInitialized);
  const variablesByKey = useVariables();
  const variables = useSelector(selectVariables);
  const shouldShow = useSelector(selectShouldShowOnboardingNewsletter);
  const shouldShowRef = useRef(shouldShow);
  shouldShowRef.current = shouldShow;
  const texts = useMemo(
    () => getOnboardingNewsletterTexts(variablesByKey),
    [variablesByKey],
  );
  const image = useMemo(
    () => getOnboardingNewsletterImage(variables),
    [variables],
  );
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [isStatusReady, setIsStatusReady] = useState(false);
  const confirmedForSubRef = useRef<string | null>(null);

  const profileSub = profile?.sub;
  const isEligible = Boolean(profile && isEligibleProfile(profile));

  useEffect(() => {
    if (!isDataInitialized) {
      return undefined;
    }

    if (!profileSub || !isEligible) {
      confirmedForSubRef.current = null;
      dispatch(setShouldShowOnboardingNewsletter(false));
      setIsStatusReady(true);

      return undefined;
    }

    // Session/Redis false is authoritative — skip Brevo.
    if (!shouldShowRef.current) {
      confirmedForSubRef.current = null;
      setIsStatusReady(true);

      return undefined;
    }

    let cancelled = false;
    const alreadyShowing = confirmedForSubRef.current === profileSub;

    // Keep the modal visible while revalidating; only hide until first confirm.
    if (!alreadyShowing) {
      setIsStatusReady(false);
    }

    const loadStatus = async () => {
      try {
        const response = await fetch('/api/account/onboarding');
        const body: unknown = await response.json().catch(() => null);
        const status = parseStatus(body);

        if (cancelled) {
          return;
        }

        if (response.ok && status) {
          confirmedForSubRef.current = status.shouldShow ? profileSub : null;
          dispatch(setShouldShowOnboardingNewsletter(status.shouldShow));
          setIsStatusReady(true);

          return;
        }

        if (!alreadyShowing) {
          confirmedForSubRef.current = null;
          dispatch(setShouldShowOnboardingNewsletter(false));
          setIsStatusReady(true);
        }
      } catch {
        if (!cancelled && !alreadyShowing) {
          confirmedForSubRef.current = null;
          dispatch(setShouldShowOnboardingNewsletter(false));
          setIsStatusReady(true);
        }
      }
    };

    loadStatus();

    return () => {
      cancelled = true;
    };
  }, [dispatch, isDataInitialized, profileSub, isEligible]);

  const closeModal = useCallback(() => {
    confirmedForSubRef.current = null;
    dispatch(setShouldShowOnboardingNewsletter(false));
  }, [dispatch]);

  const closeSuccessModal = useCallback(() => {
    setShowSuccessModal(false);
  }, []);

  const dismiss = useCallback(
    async (mode: 'permanent' | 'soft'): Promise<boolean> => {
      // UX: always close after the request settles, even if Redis persist failed.
      try {
        const response = await fetch('/api/account/onboarding/dismiss', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ mode }),
        });

        closeModal();

        return response.ok;
      } catch {
        closeModal();

        return false;
      }
    },
    [closeModal],
  );

  const handleSubscribe =
    useCallback(async (): Promise<OnboardingSubscribeResult> => {
      try {
        const response = await fetch('/api/account/newsletter', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'request_confirmation' }),
        });

        if (response.status === 401) {
          return 'session_expired';
        }

        if (response.status === 429) {
          return 'rate_limited';
        }

        if (!response.ok) {
          return 'error';
        }

        closeModal();
        setShowSuccessModal(true);

        return 'success';
      } catch {
        return 'error';
      }
    }, [closeModal]);

  if (!profile || !isEligibleProfile(profile)) {
    return null;
  }

  return (
    <>
      {showSuccessModal && (
        <ProfileSaveSuccessModal
          title={texts.successTitle}
          message={texts.successMessage}
          onClose={closeSuccessModal}
        />
      )}

      {isStatusReady && shouldShow && (
        <OnboardingNewsletterModal
          texts={texts}
          image={image}
          email={profile.email}
          firstName={profile.firstName ?? ''}
          lastName={profile.lastName ?? ''}
          onDismissPermanent={() => dismiss('permanent')}
          onDismissSoft={() => dismiss('soft')}
          onSubscribe={handleSubscribe}
        />
      )}
    </>
  );
};

export default OnboardingNewsletterGate;
